import React, { useState, useEffect } from 'react';
import { Select, InputNumber, Tag, Space, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

export interface GiftCardPlan {
    id: number | string;
    planName: string;
    [key: string]: any;
}

// 定义礼品卡项类型 - 添加了 planName
interface GiftCardItem {
    /** id */
    id: string;
    /** 名称 - 仅用于保存，不用于显示 */
    planName?: string;
    /** 数量 */
    quantity: number;
    faceValue?:number;
}

interface GiftCardSelectProps {
    value?: string; // JSON字符串格式：'[{"id":"aaa","planName":"方案A","quantity":1},...]'
    onChange?: (value: string) => void;
    dataSource: any[];
    placeholder?: string;
    disabled?: boolean;
}

const GiftCardSelect: React.FC<GiftCardSelectProps> = ({
    value = '[]',
    onChange,
    dataSource = [],
    placeholder = '请选择礼品卡方案',
    disabled = false,
}) => {
    // 解析JSON字符串为礼品卡项数组
    const parseValue = (jsonStr: string): GiftCardItem[] => {
        if (!jsonStr || typeof jsonStr !== 'string') {
            return [];
        }

        try {
            const parsed = JSON.parse(jsonStr);

            // 验证是否为数组
            if (!Array.isArray(parsed)) {
                console.warn('GiftCardSelect: value 应该是一个数组');
                return [];
            }

            // 过滤和转换有效数据
            return parsed
                .filter((item: any) =>
                    item &&
                    item.id !== null &&
                    item.id !== undefined
                )
                .map((item: any) => ({
                    id: String(item.id),
                    planName: item.planName || '', // 保留 planName
                    quantity: Number.isInteger(item.quantity) && item.quantity > 0
                        ? item.quantity
                        : 1
                }));
        } catch (error) {
            console.warn('GiftCardSelect: JSON 解析失败', error);
            return [];
        }
    };

    // 将礼品卡项数组格式化为JSON字符串（包含 planName）
    const formatValue = (items: GiftCardItem[]): string => {
        if (!items.length) {
            return '[]';
        }

        // 为每个项添加 planName
        const itemsWithPlanName = items.map(item => {
            // 从 dataSource 中查找对应的 planName
            const sourceItem = dataSource.find(d => String(d.id) === item.id);
            return {
                ...item,
                planName: sourceItem?.planName || item.planName || '',
                faceValue:sourceItem?.faceValue ||item.faceValue

            };
        });

        return JSON.stringify(itemsWithPlanName);
    };

    // 解析初始值
    const initialItems = parseValue(value);

    // 状态管理
    const [selectedIds, setSelectedIds] = useState<string[]>(
        initialItems.map(item => item.id)
    );

    const [quantities, setQuantities] = useState<Record<string, number>>(() => {
        const qty: Record<string, number> = {};
        initialItems.forEach(item => {
            qty[item.id] = item.quantity;
        });
        return qty;
    });

    // 监听外部value变化
    useEffect(() => {
        const items = parseValue(value);
        setSelectedIds(items.map(item => item.id));

        const newQuantities: Record<string, number> = {};
        items.forEach(item => {
            newQuantities[item.id] = item.quantity;
        });
        setQuantities(newQuantities);
    }, [value]);

    // 数量变化处理
    const handleQuantityChange = (id: string, newQuantity: number | null) => {
        if (newQuantity === null || newQuantity < 1) {
            return;
        }

        const newQuantities = { ...quantities, [id]: newQuantity };
        setQuantities(newQuantities);

        if (onChange) {
            const items = selectedIds.map(itemId => ({
                id: itemId,
                planName: '', // 临时为空，formatValue 会补充
                quantity: newQuantities[itemId] || 1
            }));
            onChange(formatValue(items));
        }
    };

    // 选择变化处理
    const handleSelectChange = (ids: string[]) => {
        setSelectedIds(ids);

        const newQuantities = { ...quantities };

        // 确保新选中的项有默认数量
        ids.forEach(id => {
            if (!newQuantities[id]) {
                newQuantities[id] = 1;
            }
        });

        // 移除未选中的项的数量
        Object.keys(newQuantities).forEach(id => {
            if (!ids.includes(id)) {
                delete newQuantities[id];
            }
        });

        setQuantities(newQuantities);

        if (onChange) {
            const items = ids.map(itemId => ({
                id: itemId,
                planName: '', // 临时为空，formatValue 会补充
                quantity: newQuantities[itemId] || 1
            }));
            onChange(formatValue(items));
        }
    };

    // 处理移除单个选项
    const handleDeselect = (removedId: string) => {
        const newIds = selectedIds.filter(id => id !== removedId);
        handleSelectChange(newIds);
    };

    // 清空所有选择
    const handleClear = () => {
        handleSelectChange([]);
    };

    // 自定义标签渲染 - 不显示 planName
    const tagRender = (props: any) => {
        const { label, value: tagValue, closable, onClose } = props;
        const quantity = quantities[tagValue] || 1;

        return (
            <Tag
                closable={closable && !disabled}
                onClose={onClose}
                style={{
                    marginRight: 8,
                    marginBottom: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                }}
            >
                <span>{label}</span> {/* 只显示 label，不显示 planName */}
                <InputNumber
                    min={1}
                    size="small"
                    value={quantity}
                    onChange={(val) => handleQuantityChange(tagValue, val)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 60 }}
                    disabled={disabled}
                />
            </Tag>
        );
    };

    return (
        <Select
            mode="multiple"
            value={selectedIds}
            onChange={handleSelectChange}
            onDeselect={handleDeselect}
            onClear={handleClear}
            tagRender={tagRender}
            placeholder={placeholder}
            disabled={disabled}
            allowClear
            style={{ width: '100%' }}
            suffixIcon={
                <Tooltip title="数据格式: [{'id':'方案ID','planName':'方案名称','quantity':数量}, ...]">
                    <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
            }
        >
            {dataSource.map((item: any) => (
                <Select.Option
                    key={item.id}
                    value={String(item.id)}
                    disabled={disabled}
                >
                    {item.planName}
                </Select.Option>
            ))}
        </Select>
    );
};

export default GiftCardSelect;